import { inject } from '@angular/core'
import { CanActivateFn, Router, UrlTree } from '@angular/router'
import { map } from 'rxjs'
import { FeatureAccessService } from '../services/feature-access.service'

/**
 * Route guard that admits navigation only when the current plan includes the
 * named feature (a key of `FeatureAccessService.planFeatures`).
 *
 * This is a billing entitlement, so the merchant is sent somewhere that can
 * *sell* the upgrade rather than being told the page doesn't exist.
 *
 * Landing pages that render their own `<app-upgrade-prompt>` (the convention
 * for a section's index route) don't need this guard at all — it exists for the
 * sub-routes behind them, which have no upgrade surface of their own and would
 * otherwise be reachable by deep link. Those redirect to the section index,
 * where the upgrade prompt takes over.
 *
 * Usage:
 *   {
 *     path: 'affiliates/links/add',
 *     canActivate: [AuthGuard, requirePlanFeature('affiliates', '/affiliates')],
 *     ...
 *   }
 */
export const requirePlanFeature =
  (featureKey: string, redirectTo: string = '/home'): CanActivateFn =>
  () => {
    const featureAccess = inject(FeatureAccessService)
    const router = inject(Router)
    return featureAccess.hasFeature(featureKey).pipe(map((hasAccess): boolean | UrlTree => hasAccess || router.parseUrl(redirectTo)))
  }
